import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import type { SelectOption } from "@/components/form-generator"
import type { documentTypeEnum } from "../../../api/db/schema/document"

type DocumentType = (typeof documentTypeEnum.enumValues)[number]

export const FINAL_SELECTABLE_DOCUMENT_TYPES = [
  "BANK_STATEMENT",
  "COMPANY_BYLAWS",
  "COMPANY_FINANCIAL_STATEMENT",
  "COMPANY_INCORPORATION",
  "COMPANY_PARTNERSHIP_AGREEMENT",
  "COMPANY_SHAREHOLDER_AGREEMENT",
  "COMPANY_TAX",
  "COMPANY_WILL",
  "DEED",
  "ESTATE_PLAN",
  "FINANCIAL_PLAN",
  "FUNERAL_PLAN",
  "INSURANCE_POLICY",
  "INVESTMENT_STATEMENT",
  "LAND_TITLE",
  "LAST_WILL_AND_TESTAMENT",
  "LOAN_DOCUMENT",
  "MEDICAL_DIRECTIVE",
  "MORTGAGE_DOCUMENT",
  "OTHER",
  "PERSONAL_TAX",
  "POWER_OF_ATTORNEY",
  "PROPERTY_ASSESSMENT",
  "PROPERTY_RECORD",
  "RETIREMENT_PLAN",
  "SERVICE_AGREEMENT",
  "TAX_PLAN",
  "TRUST",
  "TRUST_TAXES",
  "UNCLASSIFIED",
] as const satisfies readonly DocumentType[]

export const PROCESSABLE_UPLOAD_NOTICE_DOCUMENT_TYPES = [
  "BANK_STATEMENT",
  "COMPANY_BYLAWS",
  "COMPANY_FINANCIAL_STATEMENT",
  "COMPANY_INCORPORATION",
  "COMPANY_PARTNERSHIP_AGREEMENT",
  "COMPANY_SHAREHOLDER_AGREEMENT",
  "COMPANY_TAX",
  "COMPANY_WILL",
  "ESTATE_PLAN",
  "FINANCIAL_PLAN",
  "INSURANCE_POLICY",
  "INVESTMENT_STATEMENT",
  "LAST_WILL_AND_TESTAMENT",
  "PROPERTY_ASSESSMENT",
  "RETIREMENT_PLAN",
  "TAX_PLAN",
  "TRUST",
] as const satisfies readonly DocumentType[]

const processableUploadNoticeDocumentTypeSet = new Set<string>(
  PROCESSABLE_UPLOAD_NOTICE_DOCUMENT_TYPES,
)

export function isDocumentTypeProcessableForUploadNotice(
  value: string | null | undefined,
): boolean {
  return value ? processableUploadNoticeDocumentTypeSet.has(value) : false
}

/**
 * Hook to get translated document type options for select fields.
 * Follows the pattern from asset-enums.ts
 */
export function useDocumentTypeOptions(): readonly SelectOption[] {
  const { t } = useTranslation()

  return useMemo(
    () =>
      FINAL_SELECTABLE_DOCUMENT_TYPES.map((type) => ({
        id: type,
        label: t("components.document.enums.documentType.option", { context: type }),
      })),
    [t],
  )
}

/**
 * Format a document type enum value for display.
 * Uses i18n translation.
 */
export function useFormatDocumentType() {
  const { t } = useTranslation()

  return (enumValue: string) => {
    return t("components.document.enums.documentType.option", { context: enumValue })
  }
}
